import { useCallback, useEffect, useRef } from 'react';

import { getAllFieldsTouched } from '../../utils';
import {
  getChangedEducationFieldNames,
  getChangedEducationFieldValues,
  getEducationValuesWithFieldOverride,
  getMergedEducationPatchData,
  getSavedEducationFieldValues,
  isEducationDateFieldValid,
} from '../utils';

import type { SaveField } from '../../types';
import type { EducationFormValues, EducationSavedFieldValues } from '../types';
import type { SaveEducationFieldOnBlurParams } from './types';

export default function useSaveEducationFieldOnBlur({
  formik,
  initialValues,
  validationSchema,
  onUpdate,
}: SaveEducationFieldOnBlurParams) {
  const savedFieldValuesRef = useRef<EducationSavedFieldValues>(
    getSavedEducationFieldValues(initialValues),
  );
  const savingPromiseRef = useRef(Promise.resolve());

  useEffect(() => {
    savedFieldValuesRef.current = getSavedEducationFieldValues(initialValues);
  }, [initialValues]);

  const performEducationInfoSave = useCallback(
    async (values: EducationFormValues) => {
      const previousSave = savingPromiseRef.current;
      const currentSave = (async () => {
        try {
          await previousSave;
        } catch {
          // Swallow prior save errors so this save still proceeds.
        }

        const changedSavedValues = getChangedEducationFieldValues(
          values,
          savedFieldValuesRef.current,
        );
        if (Object.keys(changedSavedValues).length === 0) {
          return;
        }

        const didUpdate = await onUpdate(
          getMergedEducationPatchData(values, getChangedEducationFieldNames(changedSavedValues)),
        );
        if (!didUpdate) {
          return;
        }

        const errors = await formik.validateForm(values);
        formik.setErrors(errors);
        formik.setTouched(getAllFieldsTouched(values), false);
        savedFieldValuesRef.current = {
          ...savedFieldValuesRef.current,
          ...changedSavedValues,
        };
      })();

      savingPromiseRef.current = currentSave;
      await currentSave;
    },
    [formik, onUpdate],
  );

  const saveField: SaveField<EducationFormValues> = useCallback(
    async (name, value) => {
      const values = getEducationValuesWithFieldOverride(formik.values, name, value);

      if (!isEducationDateFieldValid(name, values)) {
        return;
      }
      try {
        await validationSchema.validateAt(name, values);
      } catch {
        return;
      }

      await performEducationInfoSave(values);
    },
    [formik, performEducationInfoSave, validationSchema],
  );

  return { saveField };
}
